import * as React from 'react';
import {
    Box,
    Button, Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton, List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack, Typography,
    Checkbox,
    debounce
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEditor } from '@tiptap/react';
import { RichTextContainer, tiptapExtensions } from '../../../../app/RichTextEditor';
import { ValidatedObject } from '../../../project/validationProcess/validatedObjectApi';
import { APIError, AppId, OptionsFlag } from '../../../../app/appTypes';
import { Device } from '../../../device/deviceSlice';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import produce from "immer";
import { ReactLocalization, useLocalization } from '@fluent/react';
import { getValidationStatusLabel } from '../../../computerizedSystems/components/ObjectValidationStatusChip';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { blocksSelector, isDocEditable, selectDocument } from '../../documentSlice';
import { formatDate, impossible } from '../../../../app/Utils';
import { ComputerizedSystem } from '../../../computerizedSystems/computerizedSystemsApi';
import { GampCategory, GAMP_DICTIONARY } from '../../../computerizedSystems/gampDictionary';
import { updateDocBlock } from '../../documentsApi';
import { unwrapResult } from '@reduxjs/toolkit';
import { store } from '../../../../app/store';
import { Localized } from "@fluent/react";

// ---- Typy ----
export type DeviceSpec = {
    key: string;
    label: string;
    value: string;
};

export interface DeviceDataSectionProps {
    validatedObject: ValidatedObject
    initialText?: string
    sectionId: AppId
}

const getDeviceFieldLabel: (field: keyof Device, l10n: ReactLocalization) => string = (field, l10n) => {
    switch (field) {
        case 'id':
            return 'Id'
        case 'guid':
            return 'Guid'
        case 'name':
            return l10n.getString('devices-name', null, 'Nazwa')
        case 'model':
            return l10n.getString('devices-model', null, 'Model')
        case 'unknownNo':
            return l10n.getString('devices-unknown-number', null, 'Nieznany numer fabryczny')
        case 'serialNo':
            return l10n.getString('devices-serial-number', null, 'Nr fabryczny')
        case 'description':
            return l10n.getString('devices-description', null, 'Opis')
        case 'manufacturer':
            return l10n.getString('devices-producer', null, 'Producent')
        case 'structureId':
            return l10n.getString('device-type-name', null, 'Nazwa typu urządzenia')
        case 'productionYear':
            return l10n.getString('devices-production-date', null, 'Data produkcji')
        case 'validationStatus':
            return l10n.getString('devices-validation-status', null, 'Status walidacji')
        case 'warrantyStatus':
            return l10n.getString('devices-warranty-status', null, 'Status gwarancji')
        case 'isGxpRelevant':
            return l10n.getString('devices-is-gx-relevant', null, 'GxP')
        case 'supplierId':
            return l10n.getString('supplier', null, "Dostawca")
        case 'supplierName':
            return l10n.getString('devices-supplier-name', null, 'Nazwa dostawcy')
    }
}

const getComputerizedSystemFieldLabel: (field: keyof ComputerizedSystem, l10n: ReactLocalization) => string = (field, l10n) => {
    switch (field) {
        case 'id':
            return 'Id'
        case 'name':
            return l10n.getString('cs-name', null, 'Nazwa')
        case 'itOwnerId':
            return l10n.getString('cs-it-owner', null, 'Właściciel IT')
        case 'description':
            return l10n.getString('cs-description', null, 'Opis')
        case 'department':
            return l10n.getString('cs-department-long', null, 'Dział odpowiedzialny za system')
        case 'status':
            return l10n.getString('cs-status', null, 'Status')
        case 'isGxpRelevant':
            return l10n.getString('cs-is-gxp', null, 'System GxP')
        case 'criticality':
            return l10n.getString('cs-criticality', null, 'Krytyczność')
        case 'validationStatus':
            return l10n.getString('cs-validation-status', null, 'Status walidacji')
        case 'validationDate':
            return l10n.getString('cs-validation-date', null, 'Data walidacji')
        case 'nextReviewDate':
            return l10n.getString('cs-next-review-date', null, 'Data kolejnego sprawdzenia')
        case 'softwareVersion':
            return l10n.getString('cs-version', null, 'Wersja')
        case 'vendor':
            return l10n.getString('cs-vendor', null, 'Sprzedawca')
        case 'backupDescription':
            return l10n.getString('cs-backup', null, 'Backup')
        case 'structureId':
            return ''
        case 'gampCategory':
            return ''
        case 'createdBy':
            return ''
        case 'createdByFullName':
            return ''
        case 'createDate':
            return ''
        default:
            return ''
    }
}

const escapeHtml = (s: string | null) =>
    s ? s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
        : ''

const buildRow = (label: string, value: string): string => {
    return `<tr><td>${escapeHtml(label)}</td><td>${escapeHtml(value)}</td></tr>`
}

const getGampCatLabel = (c: GampCategory): string => {
    return GAMP_DICTIONARY.find(x => x.key === c)?.text.pl.label ?? ''
}

export default function DeviceDataSectionMock({
    validatedObject,
    sectionId,
    initialText = '',
}: DeviceDataSectionProps) {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()
    const { language } = header
    const isEditable = useAppSelector(isDocEditable)
    const [open, setOpen] = React.useState(false);
    const [error, setError] = useState<APIError | null>(null)

    const docId = header.id
    const autosave = useCallback(debounce((content: string) => {
        dispatch(updateDocBlock({
            sectionId,
            docId,
            description: content,
        }))
            .then(unwrapResult)
            .catch(err => setError(err as APIError))
    }, 300), [sectionId, docId])

    const editor = useEditor({
        extensions: tiptapExtensions,
        content: initialText,
        editable: true,
        onUpdate: (e) => {                   // callback to get the updated value
            autosave(e.editor.getHTML())
        },
    });

    useEffect(() => {
        if (editor) {
            const content = blocksSelector.selectById(store.getState(), sectionId)?.description ?? ''
            editor.commands.setContent(content)
        }
    }, [editor, sectionId])

    const handleInsertDeviceTable = (flags: OptionsFlag<Device>, device: Device) => {
        if (!editor) return;

        let rows: string[] = []
        if (flags.name) {
            rows.push(buildRow(getDeviceFieldLabel('name', l10n), device.name))
        }
        if (flags.description) {
            rows.push(buildRow(getDeviceFieldLabel('description', l10n), device.description))
        }
        if (flags.model) {
            rows.push(buildRow(getDeviceFieldLabel('model', l10n), device.model))
        }
        if (flags.productionYear) {
            rows.push(buildRow(getDeviceFieldLabel('productionYear', l10n), device.productionYear.toString()))
        }
        if (flags.serialNo) {
            rows.push(buildRow(getDeviceFieldLabel('serialNo', l10n), device.serialNo))
        }
        if (flags.manufacturer) {
            rows.push(buildRow(getDeviceFieldLabel('manufacturer', l10n), device.manufacturer))
        }
        if (flags.supplierName) {
            rows.push(buildRow(getDeviceFieldLabel('supplierName', l10n), device.supplierName))
        }
        if (flags.isGxpRelevant) {
            rows.push(buildRow(getDeviceFieldLabel('isGxpRelevant', l10n), device.isGxpRelevant ? l10n.getString('yes') : l10n.getString('no')))
        }
        if (flags.warrantyStatus) {
            rows.push(buildRow(getDeviceFieldLabel('warrantyStatus', l10n), device.warrantyStatus))
        }
        if (flags.validationStatus) {
            const lbl = device.validationStatus ? getValidationStatusLabel(language === 'PL' ? 'pl' : 'en', device.validationStatus) : ''
            rows.push(buildRow(getDeviceFieldLabel('validationStatus', l10n), lbl))
        }

        const table =
            `<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;width:100%;">
            <thead><tr>
            <th style="text-align:left;">${l10n.getString('devices-parameter-name')}</th>
            <th style="text-align:left;">${l10n.getString('devices-parameter-value')}</th>
            </tr></thead>
            <tbody>${rows.join('')}</tbody>
            </table>`

        if (rows.length === 0) { setOpen(false); return; }

        const currentHtml = editor.getHTML();

        editor.commands.setContent(currentHtml + table, false);

        dispatch(updateDocBlock({
            sectionId,
            docId,
            description: editor.getHTML(),
        }))
            .then(unwrapResult)
            .catch(err => setError(err as APIError))
            .finally(() => setOpen(false))
    }

    const handleInsertComputerizedSystemTable = (flags: OptionsFlag<ComputerizedSystem>, data: ComputerizedSystem) => {
        if (!editor) return;

        let rows: string[] = []
        if (flags.name) {
            rows.push(buildRow(getComputerizedSystemFieldLabel('name', l10n), data.name))
        }
        if (flags.description) {
            rows.push(buildRow(getComputerizedSystemFieldLabel('description', l10n), data?.description ?? ''))
        }
        if (flags.itOwnerId) {
            rows.push(buildRow(getComputerizedSystemFieldLabel('itOwnerId', l10n), data?.itOwnerId ?? ''))
        }
        if (flags.department) {
            rows.push(buildRow(getComputerizedSystemFieldLabel('department', l10n), data?.department ?? ''))
        }
        if (flags.status) {
            rows.push(buildRow(getComputerizedSystemFieldLabel('status', l10n), data?.status ?? ''))
        }
        if (flags.isGxpRelevant) {
            rows.push(buildRow(getComputerizedSystemFieldLabel('isGxpRelevant', l10n), data.isGxpRelevant ? l10n.getString('yes') : l10n.getString('no')))
        }
        if (flags.criticality) {
            rows.push(buildRow(getComputerizedSystemFieldLabel('criticality', l10n), data?.criticality ?? ''))
        }
        if (flags.validationStatus) {
            const lbl = data.validationStatus ? getValidationStatusLabel(language === 'PL' ? 'pl' : 'en', data.validationStatus) : ''
            rows.push(buildRow(getComputerizedSystemFieldLabel('validationStatus', l10n), lbl))
        }
        if (flags.validationDate) {
            rows.push(buildRow(getComputerizedSystemFieldLabel('validationDate', l10n), data.validationDate ? formatDate(data.validationDate) : ''))
        }
        if (flags.nextReviewDate) {
            rows.push(buildRow(getComputerizedSystemFieldLabel('nextReviewDate', l10n), data.nextReviewDate ? formatDate(data.nextReviewDate) : ''))
        }
        if (flags.softwareVersion) {
            rows.push(buildRow(getComputerizedSystemFieldLabel('softwareVersion', l10n), data?.softwareVersion ?? ''))
        }
        if (flags.vendor) {
            rows.push(buildRow(getComputerizedSystemFieldLabel('vendor', l10n), data?.vendor ?? ''))
        }
        if (flags.backupDescription) {
            rows.push(buildRow(getComputerizedSystemFieldLabel('backupDescription', l10n), data?.backupDescription ?? ''))
        }
        if (flags.gampCategory) {
            rows.push(buildRow(getComputerizedSystemFieldLabel('gampCategory', l10n), getGampCatLabel(data.gampCategory)))
        }

        const table =
            `<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;width:100%;">
            <thead><tr>
            <th style="text-align:left;">${l10n.getString('devices-parameter-name')}</th>
            <th style="text-align:left;">${l10n.getString('devices-parameter-value')}</th>
            </tr></thead>
            <tbody>${rows.join('')}</tbody>
            </table>`

        if (rows.length === 0) { setOpen(false); return; }

        const currentHtml = editor.getHTML();

        editor.commands.setContent(currentHtml + table, false);

        dispatch(updateDocBlock({
            sectionId,
            docId,
            description: editor.getHTML(),
        }))
            .then(unwrapResult)
            .catch(err => setError(err as APIError))
            .finally(() => setOpen(false))
    }

    const closeDialog = () => setOpen(false)

    let dialog: ReactNode = <></>
    if (open) {
        if (validatedObject.type === 'DEVICE') {
            dialog = <DeviceFieldPicker data={validatedObject.data} ok={(flags) => handleInsertDeviceTable(flags, validatedObject.data)} cancel={closeDialog} />
        } else if (validatedObject.type === 'COMPUTERIZED_SYSTEM') {
            dialog = <ComputerizedSystemsFieldPicker data={validatedObject.data} ok={(flags) => handleInsertComputerizedSystemTable(flags, validatedObject.data)} cancel={closeDialog} />
        }
    }

    const insertTableButton = useMemo(() => {
        return isEditable
            ? <Button variant='contained' size='small' onClick={() => setOpen(true)}>
                <Localized id="document-insert-object-parameters">Wstaw parametry urządzenia</Localized>
            </Button>
            : <></>
    }, [isEditable])

    return (
        <Box>
            <RichTextContainer label={undefined} editor={editor} actions={insertTableButton} />

            {/* Dialog z listą danych technicznych */}
            {dialog}
        </Box>
    );
}

interface FieldPicker<Type> {
    ok: (options: OptionsFlag<Type>) => void
    cancel: () => void
    data: Type
}

const DeviceFieldPicker = (props: FieldPicker<Device>) => {
    const { data: device } = props
    const { name, description, isGxpRelevant, manufacturer, model, productionYear, serialNo, supplierName, warrantyStatus } = device

    const { l10n } = useLocalization()

    const [flags, setFlags] = useState<OptionsFlag<Device>>({
        id: true,
        description: true,
        guid: true,
        isGxpRelevant: true,
        manufacturer: true,
        model: true,
        name: true,
        productionYear: true,
        unknownNo: true,
        serialNo: true,
        structureId: true,
        supplierId: true,
        supplierName: true,
        validationStatus: true,
        warrantyStatus: true,
    })

    const handleSelectAllVisible = () => {
        setFlags({
            id: true,
            description: true,
            guid: true,
            isGxpRelevant: true,
            manufacturer: true,
            model: true,
            name: true,
            productionYear: true,
            unknownNo: true,
            serialNo: true,
            structureId: true,
            supplierId: true,
            supplierName: true,
            validationStatus: true,
            warrantyStatus: true,
        })
    }

    const handleClearVisible = () => {
        setFlags({
            id: false,
            description: false,
            guid: false,
            isGxpRelevant: false,
            manufacturer: false,
            model: false,
            name: false,
            productionYear: false,
            unknownNo: false,
            serialNo: false,
            structureId: false,
            supplierId: false,
            supplierName: false,
            validationStatus: false,
            warrantyStatus: false,
        })
    }

    const toggle: (field: keyof Device) => void = (field) => {
        setFlags(produce(draft => {
            draft[field] = !draft[field]
        }))
    }

    return <Dialog open={true} fullWidth maxWidth="sm">
        <DialogTitle
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
            <Localized id="choose-technical-data">_Wybierz dane techniczne</Localized>
            <IconButton onClick={() => props.cancel()}>
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent dividers>
            <Stack direction="row" spacing={1} mt={1} mb={1}>
                <Button size="small" onClick={handleSelectAllVisible}>
                    Zaznacz wszystko
                </Button>
                <Button size="small" onClick={handleClearVisible}>
                    Wyczyść wszystko
                </Button>
                {/* <Chip size="small" label={`Wybrane: ${selected.size}`} /> */}
            </Stack>

            <Divider />
            <List dense disablePadding>
                <ListItem
                    key='name'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{name}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('name')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['name']} />
                        </ListItemIcon>
                        <ListItemText primary={getDeviceFieldLabel('name', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='description'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{description}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('description')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['description']} />
                        </ListItemIcon>
                        <ListItemText primary={getDeviceFieldLabel('description', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='model'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{model}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('model')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['model']} />
                        </ListItemIcon>
                        <ListItemText primary={getDeviceFieldLabel('model', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='manufacturer'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{manufacturer}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('manufacturer')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['manufacturer']} />
                        </ListItemIcon>
                        <ListItemText primary={getDeviceFieldLabel('manufacturer', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='production-year'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{productionYear}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('productionYear')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['productionYear']} />
                        </ListItemIcon>
                        <ListItemText primary={getDeviceFieldLabel('productionYear', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='serial-no'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{serialNo}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('serialNo')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['serialNo']} />
                        </ListItemIcon>
                        <ListItemText primary={getDeviceFieldLabel('serialNo', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='supplier-name'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{supplierName}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('supplierName')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['supplierName']} />
                        </ListItemIcon>
                        <ListItemText primary={getDeviceFieldLabel('supplierName', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='GxP'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{isGxpRelevant}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('isGxpRelevant')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['isGxpRelevant']} />
                        </ListItemIcon>
                        <ListItemText primary={getDeviceFieldLabel('isGxpRelevant', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='warrantyStatus'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{warrantyStatus}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('warrantyStatus')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['warrantyStatus']} />
                        </ListItemIcon>
                        <ListItemText primary={getDeviceFieldLabel('warrantyStatus', l10n)} />
                    </ListItemButton>
                </ListItem>

            </List>
        </DialogContent>
        <DialogActions>
            <Button onClick={props.cancel}>Anuluj</Button>
            <Button variant="contained" onClick={() => props.ok(flags)}>
                Wstaw wybrane
            </Button>
        </DialogActions>
    </Dialog>
}

const ComputerizedSystemsFieldPicker = (props: FieldPicker<ComputerizedSystem>) => {
    const { data } = props
    const { name, description, isGxpRelevant } = data

    const { l10n } = useLocalization()

    const [flags, setFlags] = useState<OptionsFlag<ComputerizedSystem>>({
        id: true,
        name: true,
        computerizedSystemIdentifier: true,
        description: true,
        itOwnerId: true,
        department: true,
        status: true,
        isGxpRelevant: true,
        criticality: true,
        validationStatus: true,
        validationDate: true,
        nextReviewDate: true,
        softwareVersion: true,
        vendor: true,
        supplierId: true,
        supplierName: true,
        backupDescription: true,
        structureId: true,
        gampCategory: true,
        createdBy: true,
        createdByFullName: true,
        createDate: true,
    })

    const handleSelectAllVisible = () => {
        setFlags({
            id: true,
            name: true,
            computerizedSystemIdentifier: true,
            description: true,
            itOwnerId: true,
            department: true,
            status: true,
            isGxpRelevant: true,
            criticality: true,
            validationStatus: true,
            validationDate: true,
            nextReviewDate: true,
            softwareVersion: true,
            vendor: true,
            supplierId: true,
            supplierName: true,
            backupDescription: true,
            structureId: true,
            gampCategory: true,
            createdBy: true,
            createdByFullName: true,
            createDate: true,
        })
    }

    const handleClearVisible = () => {
        setFlags({
            id: false,
            name: false,
            computerizedSystemIdentifier: false,
            description: false,
            itOwnerId: false,
            department: false,
            status: false,
            isGxpRelevant: false,
            criticality: false,
            validationStatus: false,
            validationDate: false,
            nextReviewDate: false,
            softwareVersion: false,
            vendor: false,
            supplierId: false,
            supplierName: false,
            backupDescription: false,
            structureId: false,
            gampCategory: false,
            createdBy: false,
            createdByFullName: false,
            createDate: false,
        })
    }

    const toggle: (field: keyof ComputerizedSystem) => void = (field) => {
        setFlags(produce(draft => {
            draft[field] = !draft[field]
        }))
    }

    return <Dialog open={true} fullWidth maxWidth="sm">
        <DialogTitle
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
            <Localized id="choose-technical-data">_Wybierz dane techniczne</Localized>
            <IconButton onClick={() => props.cancel()}>
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent dividers>
            <Stack direction="row" spacing={1} mt={1} mb={1}>
                <Button size="small" onClick={handleSelectAllVisible}>
                    <Localized id="select-all">_select all</Localized>
                </Button>
                <Button size="small" onClick={handleClearVisible}>
                    <Localized id="clear-all">_clear all</Localized>
                </Button>
                {/* <Chip size="small" label={`Wybrane: ${selected.size}`} /> */}
            </Stack>

            <Divider />
            <List dense disablePadding>
                <ListItem
                    key='name'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{name}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('name')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['name']} />
                        </ListItemIcon>
                        <ListItemText primary={getComputerizedSystemFieldLabel('name', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='description'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{description}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('description')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['description']} />
                        </ListItemIcon>
                        <ListItemText primary={getComputerizedSystemFieldLabel('description', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='itOwnerId'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{data?.itOwnerId ?? ''}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('itOwnerId')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['itOwnerId']} />
                        </ListItemIcon>
                        <ListItemText primary={getComputerizedSystemFieldLabel('itOwnerId', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='department'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{data?.department ?? ''}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('department')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['department']} />
                        </ListItemIcon>
                        <ListItemText primary={getComputerizedSystemFieldLabel('department', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='status'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{data.status}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('status')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['status']} />
                        </ListItemIcon>
                        <ListItemText primary={getComputerizedSystemFieldLabel('status', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='GxP'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{isGxpRelevant}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('isGxpRelevant')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['isGxpRelevant']} />
                        </ListItemIcon>
                        <ListItemText primary={getComputerizedSystemFieldLabel('isGxpRelevant', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='criticality'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{data.criticality}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('criticality')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['criticality']} />
                        </ListItemIcon>
                        <ListItemText primary={getComputerizedSystemFieldLabel('criticality', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='validationStatus'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{data.validationStatus}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('validationStatus')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['validationStatus']} />
                        </ListItemIcon>
                        <ListItemText primary={getComputerizedSystemFieldLabel('validationStatus', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='validationDate'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{data.validationDate ? formatDate(data.validationDate) : ''}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('validationDate')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['validationDate']} />
                        </ListItemIcon>
                        <ListItemText primary={getComputerizedSystemFieldLabel('validationDate', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='nextReviewDate'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{data.nextReviewDate ? formatDate(data.nextReviewDate) : ''}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('nextReviewDate')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['nextReviewDate']} />
                        </ListItemIcon>
                        <ListItemText primary={getComputerizedSystemFieldLabel('nextReviewDate', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='softwareVersion'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{data?.softwareVersion ?? ''}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('softwareVersion')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['softwareVersion']} />
                        </ListItemIcon>
                        <ListItemText primary={getComputerizedSystemFieldLabel('softwareVersion', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='vendor'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{data?.vendor ?? ''}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('vendor')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['vendor']} />
                        </ListItemIcon>
                        <ListItemText primary={getComputerizedSystemFieldLabel('vendor', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='backupDescription'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{data?.backupDescription ?? ''}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('backupDescription')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['backupDescription']} />
                        </ListItemIcon>
                        <ListItemText primary={getComputerizedSystemFieldLabel('backupDescription', l10n)} />
                    </ListItemButton>
                </ListItem>

                <ListItem
                    key='gampCategory'
                    disablePadding
                    secondaryAction={<Typography variant="body2" color="text.secondary">{data.gampCategory}</Typography>}
                >
                    <ListItemButton onClick={() => toggle('gampCategory')}>
                        <ListItemIcon>
                            <Checkbox edge="start" tabIndex={-1} disableRipple checked={flags['gampCategory']} />
                        </ListItemIcon>
                        <ListItemText primary={getComputerizedSystemFieldLabel('gampCategory', l10n)} />
                    </ListItemButton>
                </ListItem>

            </List>
        </DialogContent>
        <DialogActions>
            <Button variant="contained" onClick={() => props.ok(flags)}>
                <Localized id="insert-selected">Wstaw wybrane</Localized>
            </Button>
            <Button onClick={props.cancel}>
                <Localized id="cancel">_Anuluj</Localized>
            </Button>
        </DialogActions>
    </Dialog>
}
